import React, { useEffect, useState, useCallback } from "react";
import { api, API, formatApiError } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import { toast } from "sonner";
import {
  Mail, Users, Download, Trash2, LogOut, RefreshCw, Loader2, Home, Search,
} from "lucide-react";

function formatDate(d) {
  try {
    return new Date(d).toLocaleString(undefined, {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return String(d);
  }
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("messages");
  const [stats, setStats] = useState({ messages: 0, subscribers: 0 });
  const [messages, setMessages] = useState([]);
  const [subs, setSubs] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, m, sb] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/messages"),
        api.get("/admin/subscribers"),
      ]);
      setStats(s.data);
      setMessages(m.data);
      setSubs(sb.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const deleteMessage = async (id) => {
    if (!window.confirm("Delete this message? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/messages/${id}`);
      setMessages((m) => m.filter((x) => x.id !== id));
      setStats((s) => ({ ...s, messages: Math.max(0, s.messages - 1) }));
      toast.success("Message deleted");
    } catch (e) {
      toast.error("Could not delete.");
    }
  };

  const deleteSub = async (id) => {
    if (!window.confirm("Remove this subscriber?")) return;
    try {
      await api.delete(`/admin/subscribers/${id}`);
      setSubs((m) => m.filter((x) => x.id !== id));
      setStats((s) => ({ ...s, subscribers: Math.max(0, s.subscribers - 1) }));
      toast.success("Subscriber removed");
    } catch (e) {
      toast.error("Could not remove.");
    }
  };

  const exportCsv = (kind) => {
    window.open(`${API}/admin/${kind}.csv`, "_blank");
  };

  const filtered = (rows, fields) =>
    !q
      ? rows
      : rows.filter((r) =>
          fields.some((f) => String(r[f] || "").toLowerCase().includes(q.toLowerCase()))
        );

  const msgs = filtered(messages, ["name", "email", "message"]);
  const subsFiltered = filtered(subs, ["email"]);

  return (
    <div className="min-h-screen paper-bg pb-20" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="bg-white border-b border-[#e5dcd0] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href="/" className="font-serif text-lg text-[#1c1a1a]" data-testid="admin-back-home">
              Nitin Sawhney<span className="text-[#8b2e3e]">.</span>
              <span className="ml-3 eyebrow !text-[#4a4646]">Admin</span>
            </a>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-[#4a4646]">
              {user?.email}
            </span>
            <a
              href="/"
              className="hidden sm:inline-flex items-center gap-2 text-sm text-[#4a4646] hover:text-[#8b2e3e]"
            >
              <Home size={14} /> Site
            </a>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 border border-[#1c1a1a] hover:bg-[#1c1a1a] hover:text-white text-[#1c1a1a] px-4 py-2 text-xs tracking-[0.15em] uppercase transition-colors"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-12">
        <p className="eyebrow mb-3">Dashboard</p>
        <h1 className="font-serif text-4xl lg:text-5xl text-[#1c1a1a] tracking-tight">
          Readers who wrote in.
        </h1>

        {/* Stats */}
        <div className="grid sm:grid-cols-2 gap-6 mt-10">
          <div className="bg-white border border-[#e5dcd0] p-6 flex items-center justify-between" data-testid="stat-messages">
            <div>
              <p className="eyebrow !text-[#4a4646]">Messages</p>
              <p className="font-serif text-5xl text-[#1c1a1a] mt-2">{stats.messages}</p>
            </div>
            <Mail className="text-[#8b2e3e]" size={28} />
          </div>
          <div className="bg-white border border-[#e5dcd0] p-6 flex items-center justify-between" data-testid="stat-subscribers">
            <div>
              <p className="eyebrow !text-[#4a4646]">Newsletter subscribers</p>
              <p className="font-serif text-5xl text-[#1c1a1a] mt-2">{stats.subscribers}</p>
            </div>
            <Users className="text-[#8b2e3e]" size={28} />
          </div>
        </div>

        {/* Tabs + toolbar */}
        <div className="mt-12 flex flex-wrap items-center gap-4 justify-between">
          <div className="inline-flex border border-[#e5dcd0] bg-white">
            <button
              onClick={() => setTab("messages")}
              data-testid="tab-messages"
              className={`px-5 py-2.5 text-sm tracking-wider uppercase transition-colors ${
                tab === "messages" ? "bg-[#8b2e3e] text-white" : "text-[#1c1a1a] hover:bg-[#f0eae1]"
              }`}
            >
              Messages
            </button>
            <button
              onClick={() => setTab("subs")}
              data-testid="tab-subscribers"
              className={`px-5 py-2.5 text-sm tracking-wider uppercase transition-colors ${
                tab === "subs" ? "bg-[#8b2e3e] text-white" : "text-[#1c1a1a] hover:bg-[#f0eae1]"
              }`}
            >
              Subscribers
            </button>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4a4646]" size={14} />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search…"
                data-testid="admin-search"
                className="pl-9 pr-3 py-2 bg-white border border-[#e5dcd0] text-sm focus:outline-none focus:border-[#8b2e3e]"
              />
            </div>
            <button
              onClick={load}
              data-testid="admin-refresh-btn"
              className="p-2 border border-[#e5dcd0] bg-white text-[#1c1a1a] hover:border-[#8b2e3e] hover:text-[#8b2e3e] transition-colors"
              aria-label="Refresh"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            </button>
            <button
              onClick={() => exportCsv(tab === "messages" ? "messages" : "subscribers")}
              data-testid="admin-export-csv"
              className="inline-flex items-center gap-2 border border-[#1c1a1a] text-[#1c1a1a] hover:bg-[#1c1a1a] hover:text-white px-4 py-2 text-xs tracking-[0.15em] uppercase transition-colors"
            >
              <Download size={14} /> Export CSV
            </button>
          </div>
        </div>

        {/* Tables */}
        <div className="mt-6 bg-white border border-[#e5dcd0] overflow-hidden">
          {tab === "messages" ? (
            msgs.length === 0 ? (
              <EmptyState label="No messages yet." />
            ) : (
              <ul data-testid="messages-list" className="divide-y divide-[#e5dcd0]">
                {msgs.map((m) => (
                  <li key={m.id} className="p-6 hover:bg-[#f9f6f0]/60" data-testid={`message-row-${m.id}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-3 flex-wrap">
                          <p className="font-serif text-xl text-[#1c1a1a]">{m.name}</p>
                          <a href={`mailto:${m.email}`} className="text-sm text-[#8b2e3e] link-underline break-all">
                            {m.email}
                          </a>
                          <span className="eyebrow !text-[#4a4646] !text-[10px]">
                            {formatDate(m.created_at)}
                          </span>
                        </div>
                        <p className="mt-3 text-[#4a4646] leading-relaxed whitespace-pre-wrap break-words">
                          {m.message}
                        </p>
                      </div>
                      <button
                        onClick={() => deleteMessage(m.id)}
                        data-testid={`delete-message-${m.id}`}
                        className="shrink-0 p-2 text-[#4a4646] hover:text-white hover:bg-[#8b2e3e] border border-[#e5dcd0] hover:border-[#8b2e3e] transition-colors"
                        aria-label="Delete message"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )
          ) : subsFiltered.length === 0 ? (
            <EmptyState label="No subscribers yet." />
          ) : (
            <ul data-testid="subscribers-list" className="divide-y divide-[#e5dcd0]">
              {subsFiltered.map((s) => (
                <li
                  key={s.id}
                  data-testid={`subscriber-row-${s.id}`}
                  className="p-5 px-6 flex items-center justify-between gap-4 hover:bg-[#f9f6f0]/60"
                >
                  <div className="min-w-0">
                    <a href={`mailto:${s.email}`} className="font-serif text-lg text-[#1c1a1a] hover:text-[#8b2e3e] break-all">
                      {s.email}
                    </a>
                    <p className="eyebrow !text-[#4a4646] !text-[10px] mt-1 flex items-center gap-3">
                      <span>Subscribed {formatDate(s.created_at)}</span>
                      {s.source && s.source !== "website" && (
                        <span className="inline-block px-2 py-0.5 bg-[#f4d8d8]/60 text-[#8b2e3e] border border-[#e5c9c5]">
                          via {s.source}
                        </span>
                      )}
                    </p>
                  </div>
                  <button
                    onClick={() => deleteSub(s.id)}
                    data-testid={`delete-subscriber-${s.id}`}
                    className="shrink-0 p-2 text-[#4a4646] hover:text-white hover:bg-[#8b2e3e] border border-[#e5dcd0] hover:border-[#8b2e3e] transition-colors"
                    aria-label="Remove subscriber"
                  >
                    <Trash2 size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="p-16 text-center text-[#4a4646]" data-testid="admin-empty-state">
      <p className="font-serif italic text-xl">{label}</p>
    </div>
  );
}
